import { GatewayEntreprise } from "@conformite/gateway";
import { Button, FormField, FormRow } from "@endya-conformite/ui-kit";
import Warning from "@endya-conformite/ui-kit/src/assets/icons/exclamation-circle-fill.svg?react";
import Pencil from "@endya-conformite/ui-kit/src/assets/icons/pencil.svg?react";
import { ParcoursAction } from "@src/parcours/NavigationButtons/ParcoursAction";
import { SaveAndQuitButton } from "@src/parcours/NavigationButtons/SaveAndQuitButton";
import { ParcoursFullPageTemplate } from "@src/parcours/Template/ParcoursFullPageTemplate";
import {
  ParcoursRenouvellementStepComponentProps,
  RenouvellementStepEnum,
} from "@src/renouvellement/ParcoursRenouvellement.definition";
import { contactMediationValidationSchema } from "@src/societes/form/EntrepriseContactMediationForm";
import { useMe } from "@src/store/store.me";
import { ReactElement, useCallback } from "react";
import { RenouvellementData } from "../RenouvellementData";
import style from "./RenouvellementResumeEntreprisePage.module.scss";
import { ResumeAdresse } from "./ResumeAdresse";
import { ResumeContactMediation } from "./ResumeContactMediation";
import { ResumeIdentification } from "./ResumeIdentification";
import { ResumeInfoActivite } from "./ResumeInfoActivite";
import { ResumeInfoPersonnelle } from "./ResumeInfoPersonnelle";

type ResumeBlockProps = {
  title: string;
  children: ReactElement;
  changeStep: () => void;
  isValid?: boolean;
};

export function ResumeBlock({
  title,
  children,
  changeStep,
  isValid = true,
}: ResumeBlockProps) {
  return (
    <div className={style.resumeBlock}>
      <FormRow>
        <FormField>
          <div className={style.headerWrapper}>
            <div className={style.headerTitle}>
              {!isValid && <Warning className={style.warningIcon} />}
              <h2>{title}</h2>
            </div>
            <Button icon={<Pencil />} variant="text" onClick={changeStep}>
              Modifier
            </Button>
          </div>
        </FormField>
      </FormRow>
      {!isValid && (
        <div className={style.warningMessage}>
          Des informations obligatoires sont manquantes
        </div>
      )}

      <FormRow>
        <FormField>{children}</FormField>
      </FormRow>
    </div>
  );
}
export type ResumeBlockComponentProps = { data: RenouvellementData };

export function RenouvellementResumeEntreprisePage({
  data,
  changeStep,
  handleQuit,
}: ParcoursRenouvellementStepComponentProps) {
  const { me } = useMe();
  const handleChangeStep = useCallback(
    (step: RenouvellementStepEnum) => () => changeStep(step),
    [changeStep]
  );
  const { success: isContactMediationValid } =
    contactMediationValidationSchema.safeParse(data.resume.contactMediation);

  return (
    <ParcoursFullPageTemplate
      actions={
        <>
          <SaveAndQuitButton onClick={handleQuit} />
          <ParcoursAction
            onClick={() => {
              if (
                me?.entreprisePrincipale.typeAdhesion ===
                GatewayEntreprise.TypeAdhesion.SOCIETE_INDIVIDUELLE
              ) {
                changeStep(RenouvellementStepEnum.CHIFFRE_AFFAIRE);
              } else {
                changeStep(RenouvellementStepEnum.MISE_A_JOUR_SOCIETES);
              }
            }}
            disabled={
              me?.entreprisePrincipale.typeAdhesion ===
              GatewayEntreprise.TypeAdhesion.SOCIETE_INDIVIDUELLE
                ? !isContactMediationValid
                : false
            }
          >
            Suivant
          </ParcoursAction>
        </>
      }
    >
      <ResumeBlock
        title="Identification"
        changeStep={handleChangeStep(RenouvellementStepEnum.IDENTIFICATION)}
      >
        <ResumeIdentification data={data} />
      </ResumeBlock>

      <ResumeBlock
        title="Adresse de l'entreprise"
        changeStep={handleChangeStep(RenouvellementStepEnum.ADRESSE)}
      >
        <ResumeAdresse data={data} />
      </ResumeBlock>

      <ResumeBlock
        title="Informations personnelles"
        changeStep={handleChangeStep(
          RenouvellementStepEnum.INFORMATIONS_PERSONNELLES
        )}
      >
        <ResumeInfoPersonnelle data={data} />
      </ResumeBlock>

      {me?.entreprisePrincipale.typeAdhesion ===
        GatewayEntreprise.TypeAdhesion.SOCIETE_INDIVIDUELLE && (
        <>
          <ResumeBlock
            title="Informations sur votre activité"
            changeStep={handleChangeStep(
              RenouvellementStepEnum.INFORMATIONS_ACTIVITE
            )}
          >
            <ResumeInfoActivite data={data} />
          </ResumeBlock>

          <ResumeBlock
            title="Contact de médiation"
            changeStep={handleChangeStep(
              RenouvellementStepEnum.CONTACT_MEDIATION
            )}
            isValid={isContactMediationValid}
          >
            <ResumeContactMediation
              data={data}
              isValid={isContactMediationValid}
            />
          </ResumeBlock>
        </>
      )}
    </ParcoursFullPageTemplate>
  );
}
